import { useEffect, useRef } from 'react';
import { NodeViewWrapper } from '@tiptap/react';

import UnfurledLinkPreview from '@/shared/components/memos/UnfurledLinkPreview';
import { fetchMetadata } from '@/utilities';

import { UNFURLED_LINK_METADATA_VERSION } from '../constants';

const markdownLinkPattern = /\[[^\]]+\]\(https?:\/\/[^)]+\)/i;
const staleCaptionPattern = /^(?:caption|credit|figure|illustration|illumination|image|photo|photograph)\b/i;

const isWikipediaUrl = (url?: string) => {
    if (!url) {
        return false;
    }

    try {
        return new URL(url).hostname.toLowerCase().endsWith('wikipedia.org');
    } catch {
        return false;
    }
};

const normalizeTitle = (title?: string) => title?.replace(/\s+[-|].*$/, '').trim().toLowerCase();

const shouldRefreshMetadata = ({
    description,
    metadataVersion,
    title,
    url,
}: {
    description?: string | null;
    metadataVersion?: string | null;
    title?: string | null;
    url?: string | null;
}) => {
    if (!url || metadataVersion === UNFURLED_LINK_METADATA_VERSION) {
        return false;
    }

    const normalizedDescription = description?.trim();

    if (!normalizedDescription) {
        return true;
    }

    if (markdownLinkPattern.test(normalizedDescription) || staleCaptionPattern.test(normalizedDescription)) {
        return true;
    }

    const normalizedTitle = normalizeTitle(title ?? undefined);

    return Boolean(
        normalizedTitle &&
            isWikipediaUrl(url) &&
            !normalizedDescription.toLowerCase().includes(normalizedTitle),
    );
};

const UnfurledLinkComponent = (props: any) => {
    const { updateAttributes } = props;
    const { url, title, description, image, logo, publisher, date, author, isLoading, metadataVersion } =
        props.node.attrs;
    const refreshInFlightRef = useRef(false);
    const nodeViewAttributes = {
        'data-type': 'unfurled-link',
        'data-url': url || undefined,
        'data-title': title || undefined,
        'data-description': description || undefined,
        'data-image': image || undefined,
        'data-logo': logo || undefined,
        'data-publisher': publisher || undefined,
        'data-date': date || undefined,
        'data-author': author || undefined,
        'data-is-loading': isLoading ? 'true' : 'false',
        'data-metadata-version': metadataVersion || undefined,
    };

    useEffect(() => {
        if (
            !props.editor?.isEditable ||
            isLoading ||
            refreshInFlightRef.current ||
            !shouldRefreshMetadata({ description, metadataVersion, title, url })
        ) {
            return;
        }

        refreshInFlightRef.current = true;

        fetchMetadata(url)
            .then((metadata) => {
                const openGraph = metadata?.openGraph;

                if (!openGraph) {
                    updateAttributes({ metadataVersion: UNFURLED_LINK_METADATA_VERSION });
                    return;
                }

                updateAttributes({
                    author: openGraph.author ?? null,
                    date: openGraph.date ?? null,
                    description: openGraph.description ?? null,
                    image: openGraph.image?.url ?? null,
                    logo: openGraph.logo?.url ?? null,
                    metadataVersion: UNFURLED_LINK_METADATA_VERSION,
                    publisher: openGraph.publisher ?? null,
                    title: openGraph.title || title || url,
                    url: openGraph.url || url,
                });
            })
            .catch(() => {
                updateAttributes({ metadataVersion: UNFURLED_LINK_METADATA_VERSION });
            })
            .finally(() => {
                refreshInFlightRef.current = false;
            });
    }, [description, isLoading, metadataVersion, props.editor?.isEditable, title, updateAttributes, url]);

    return (
        <NodeViewWrapper {...nodeViewAttributes} style={{ margin: '0.75rem 0' }}>
            <UnfurledLinkPreview
                author={author}
                date={date}
                description={description}
                image={image}
                isLoading={isLoading}
                logo={logo}
                publisher={publisher}
                title={title}
                url={url}
            />
        </NodeViewWrapper>
    );
};

export default UnfurledLinkComponent;
